import { Flex, Typography } from 'antd'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { McpClientIcon } from './ClientIcon'
import { MCP_CLIENTS, type McpClientId } from './snippets'
import { useStyle } from './styles'

const { Text } = Typography

type Props = {
  value: McpClientId | null
  onChange: (client: McpClientId | null) => void
  /** Locks the list, e.g. while a freshly issued token is on screen. */
  disabled: boolean
  isUnavailable: (client: McpClientId) => boolean
  /** The open client's instructions, shown inside its card. */
  children: ReactNode
}

/** One card per client; opening one shows its instructions right under it. */
export const ClientPicker = ({
  value,
  onChange,
  disabled,
  isUnavailable,
  children
}: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyle()

  return (
    <Flex
      vertical
      gap={8}
      role="list"
      aria-label={t('workspace.aiConnectors.connect.chooseClient')}
    >
      {MCP_CLIENTS.map((client) => {
        const open = value === client
        const locked = disabled || isUnavailable(client)

        return (
          <Flex
            key={client}
            vertical
            gap={12}
            role="listitem"
            className={cx(
              styles.serviceRow,
              open && styles.serviceRowSelected,
              locked && !open && styles.clientRowLocked
            )}
          >
            <button
              type="button"
              className={styles.clientHeader}
              aria-expanded={open}
              disabled={locked}
              onClick={() => onChange(open ? null : client)}
            >
              <span className={styles.clientLogo}>
                <McpClientIcon client={client} />
              </span>
              <Flex vertical flex={1} style={{ minWidth: 0 }}>
                <Text strong>
                  {t(`workspace.aiConnectors.clients.${client}`)}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t(`workspace.aiConnectors.connect.clientHints.${client}`)}
                </Text>
              </Flex>
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {open ? children : null}
          </Flex>
        )
      })}
    </Flex>
  )
}
